import React, { useEffect } from "react";
import { Form, Input, Select, Button, Card, Divider, Alert, Space } from "antd";
import { useAppDispatch, useAppSelector } from "Store/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import {
  createPaddleProduct,
  updatePaddleProduct,
  getPaddleProduct,
  getProductEditions,
} from "../redux/thunks";
import {
  selectCurrentPaddleProduct,
  selectProductEditions,
} from "../redux/selectors";
import { setLoadingState } from "Components/Basic/full_page_loader/redux/slice";
import { setErrorState } from "Components/Basic/ErrorBoundary/redux/slice";
import { ICreatePaddleProductRequest } from "../redux/types";
import "./AddEditPaddleProductPage.index.css";

const { TextArea } = Input;
const { Option } = Select;

const AddEditPaddleProductPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { productId } = useParams<{ productId: string }>();
  const [form] = Form.useForm();

  const currentProduct = useAppSelector(selectCurrentPaddleProduct);
  const productEditions = useAppSelector(selectProductEditions);
  const isEdit = !!productId;

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(getProductEditions());

        if (isEdit && productId) {
          await dispatch(getPaddleProduct(productId)).unwrap();
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchData();
  }, [dispatch, isEdit, productId]);

  useEffect(() => {
    if (isEdit && currentProduct) {
      form.setFieldsValue({
        name: currentProduct.name,
        description: currentProduct.description,
        productEdition: currentProduct.productEdition,
      });
    }
  }, [currentProduct, isEdit, form]);

  const onFinish = async (values: any) => {
    try {
      dispatch(setLoadingState(true));

      const request: ICreatePaddleProductRequest = {
        name: values.name,
        description: values.description,
        productEdition: values.productEdition,
      };

      if (isEdit && productId) {
        await dispatch(
          updatePaddleProduct({
            productId,
            request: {
              name: request.name,
              description: request.description,
            },
          })
        ).unwrap();
      } else {
        await dispatch(createPaddleProduct(request)).unwrap();
      }

      dispatch(
        setErrorState({
          message: "Success",
          description: `Product ${
            isEdit ? "updated" : "created"
          } successfully in Paddle`,
          placement: "topRight",
          type: "success",
        })
      );

      navigate("/payment-products");
    } catch (err: any) {
      dispatch(
        setErrorState({
          message: "Error",
          description:
            err.message || `Failed to ${isEdit ? "update" : "create"} product`,
          placement: "topRight",
          type: "error",
        })
      );
    } finally {
      dispatch(setLoadingState(false));
    }
  };

  const handleCancel = () => {
    form.resetFields();
    navigate("/payment-products");
  };

  return (
    <div className="add-edit-paddle-product-page-container">
      <div className="add-edit-paddle-product-page-content">
        <Card className="add-edit-paddle-product-page-card">
          <div className="page-header">
            <Button icon={<ArrowLeftOutlined />} onClick={handleCancel}>
              Back
            </Button>
            <h2>{isEdit ? "Edit Paddle Product" : "Create Paddle Product"}</h2>
          </div>

          <Divider />

          <Alert
            message="Paddle Integration"
            description="Products are created directly in Paddle via API."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            className="add-edit-paddle-product-form"
          >
            <Form.Item
              label="Product Name"
              name="name"
              rules={[{ required: true, message: "Please enter product name" }]}
            >
              <Input placeholder="Enter product name" />
            </Form.Item>

            <Form.Item label="Description" name="description">
              <TextArea rows={3} placeholder="Enter product description" />
            </Form.Item>

            <Form.Item
              label="Product Edition"
              name="productEdition"
              rules={[
                { required: true, message: "Please select product edition" },
              ]}
            >
              <Select placeholder="Select product edition">
                {productEditions?.map((edition) => (
                  <Option key={edition.name} value={edition.name}>
                    {edition.description}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {isEdit ? "Update Product" : "Create Product"}
                </Button>
                <Button onClick={handleCancel}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default AddEditPaddleProductPage;
